// src/encounter/ambient/ambient-overlay.ts
//
// `ambientOverlay` (`?x=ambient-overlay`, src/shared/lib/encounter-flags.ts) · Samer, 4 Nov: "ambient shouldn't be in the
// intelligence rail. Use the popup overlay". Where the physician's ambient session lives, decided once from the switches, so the
// encounter page reads one answer and a test can pin it.
//
// The overlay is the existing floating window (ambient-window.tsx) and its body is the existing AmbientScribePanel: the same
// transcript, recordings (New recording, Keep both / Replace), suggestions, consent gate and Accept-into-the-note. This module only
// decides PLACEMENT and the entry points; it calls nothing.
//
// Switch OFF: exactly today's `ambientWindow` behaviour (window = ambientWindow && frameV2; header mic only without the window;
// no auto-open; a nurse keeps what she has today).

export type AmbientPlacementInput = {
  /** `ambientWindow` as this browser reads it. */
  windowSwitch: boolean;
  /** `ambientOverlay` as this browser reads it. */
  overlaySwitch: boolean;
  /** Frame v2 (the long page). The window exists only there. */
  frameV2: boolean;
  isNurse: boolean;
};

export type AmbientPlacement = {
  /** The session is in the floating window (never in the Intelligence rail). */
  windowOn: boolean;
  /** The small page-header mic is shown (as an entry point). */
  headerMic: boolean;
  /** The window opens by itself when listening starts, from any entry point. */
  autoOpen: boolean;
  /** No ambient for this user at all (no panel, no card, no mic). */
  hidden: boolean;
};

export function ambientPlacement({ windowSwitch, overlaySwitch, frameV2, isNurse }: AmbientPlacementInput): AmbientPlacement {
  // A nurse has no ambient whenever the switch is on, in every frame (checked before the frame, Codex review on #953).
  if (overlaySwitch && isNurse) return { windowOn: false, headerMic: false, autoOpen: false, hidden: true };
  // One entry point (Samer, 4 Nov: "we have two ambients"): the Start listening card is it; no header mic beside it.
  if (overlaySwitch && frameV2) return { windowOn: true, headerMic: false, autoOpen: true, hidden: false };
  const windowOn = windowSwitch && frameV2;
  return { windowOn, headerMic: !windowOn, autoOpen: false, hidden: false };
}

/** Live = the recording is running or starting (the panel's own control). */
export function ambientIsLive(control: { status?: string; starting?: boolean } | null | undefined): boolean {
  return !!control && (control.status === 'recording' || !!control.starting);
}

/** Open the window on the EDGE into live only, so the clinician's Close is kept while listening continues. */
export function shouldAutoOpen(autoOpen: boolean, wasLive: boolean, isLive: boolean): boolean {
  return autoOpen && !wasLive && isLive;
}
